<template>
  <div class="verification-code-container">
    <div class="code-header">
      <h3>{{ $t('verification.enterVerificationCode') }}</h3>
      <p class="code-instructions">
        {{ $t('verification.codeSentTo') }} <strong>{{ email }}</strong>
      </p>
    </div>

    <div class="code-input-wrapper">
      <div class="code-inputs">
        <input
          v-for="(digit, index) in codeDigits"
          :key="index"
          :ref="el => inputRefs[index] = el"
          v-model="codeDigits[index]"
          type="text"
          inputmode="numeric"
          maxlength="1"
          class="code-input"
          :class="{ 'error': hasError }"
          @input="handleInput(index, $event)"
          @keydown="handleKeydown(index, $event)"
          @paste="handlePaste"
          @focus="handleFocus(index)"
        />
      </div>
    </div>

    <div v-if="hasError" class="error-message">
      {{ errorMessage }}
      <span v-if="attemptsRemaining !== null && attemptsRemaining > 0" class="attempts-remaining">
        ({{ attemptsRemaining }} {{ $t('verification.attemptsRemaining') }})
      </span>
    </div>

    <div v-if="codeExpiresAt" class="expiration-info">
      <span class="expiration-label">{{ $t('verification.codeExpiresIn') }}</span>
      <span class="expiration-time">{{ timeRemaining }}</span>
    </div>

    <div class="code-actions">
      <button
        type="button"
        class="resend-button"
        :disabled="isResending || isLoading || !canResend"
        @click="handleResend"
      >
        <span v-if="isResending">{{ $t('verification.sendingCode') }}</span>
        <span v-else-if="!canResend && nextResendTime">
          {{ $t('verification.resendCodeIn', { time: timeUntilResend }) }}
        </span>
        <span v-else>{{ $t('verification.resendCode') }}</span>
      </button>
    </div>

    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner"></div>
      <p>{{ $t('verification.verifyingCode') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
  email: {
    type: String,
    required: true
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: null
  },
  attemptsRemaining: {
    type: Number,
    default: null
  },
  codeExpiresAt: {
    type: String,
    default: null
  },
  nextResendAvailable: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['code-complete', 'resend-requested']);

const codeDigits = ref(['', '', '', '', '', '']);
const inputRefs = ref([]);
const hasError = ref(false);
const errorMessage = ref('');
const isResending = ref(false);
const timeRemaining = ref('');
const timeUntilResend = ref('');
const expirationTimer = ref(null);
const resendTimer = ref(null);
const canResend = ref(true);

// Watch for error prop changes
watch(() => props.error, (newError) => {
  if (newError) {
    hasError.value = true;
    errorMessage.value = newError;
  } else {
    hasError.value = false;
    errorMessage.value = '';
  }
});

// Watch for code expiration
watch(() => props.codeExpiresAt, (newExpiresAt) => {
  if (newExpiresAt) {
    startExpirationTimer(newExpiresAt);
  }
});

// Watch for next resend time
watch(() => props.nextResendAvailable, (newTime) => {
  if (newTime) {
    startResendTimer(newTime);
  } else {
    canResend.value = true;
  }
});

const handleInput = (index, event) => {
  const value = event.target.value;
  
  // Only allow digits
  if (value && !/^\d$/.test(value)) {
    codeDigits.value[index] = '';
    return;
  }

  codeDigits.value[index] = value;

  // Clear error when user starts typing
  if (hasError.value) {
    hasError.value = false;
    errorMessage.value = '';
  }

  // Auto-focus next input
  if (value && index < 5) {
    focusInput(index + 1);
  }

  // Check if all digits are filled
  checkCodeComplete();
};

const handleKeydown = (index, event) => {
  // Handle backspace
  if (event.key === 'Backspace' && !codeDigits.value[index] && index > 0) {
    focusInput(index - 1);
  }
  
  // Handle arrow keys
  if (event.key === 'ArrowLeft' && index > 0) {
    event.preventDefault();
    focusInput(index - 1);
  }
  if (event.key === 'ArrowRight' && index < 5) {
    event.preventDefault();
    focusInput(index + 1);
  }
};

const handlePaste = (event) => {
  event.preventDefault();
  const pastedData = event.clipboardData.getData('text').trim();
  
  // Extract only digits
  const digits = pastedData.replace(/\D/g, '').slice(0, 6);
  
  if (digits.length === 6) {
    // Fill all inputs with pasted digits
    for (let i = 0; i < 6; i++) {
      codeDigits.value[i] = digits[i];
    }
    focusInput(5);
    checkCodeComplete();
  }
};

const handleFocus = (index) => {
  // Select text when focused
  if (inputRefs.value[index]) {
    inputRefs.value[index].select();
  }
};

const focusInput = (index) => {
  if (inputRefs.value[index]) {
    inputRefs.value[index].focus();
  }
};

const checkCodeComplete = () => {
  const code = codeDigits.value.join('');
  if (code.length === 6) {
    emit('code-complete', code);
  }
};

const handleResend = () => {
  if (!canResend.value || isResending.value) return;
  emit('resend-requested');
};

const startExpirationTimer = (expiresAt) => {
  if (expirationTimer.value) {
    clearInterval(expirationTimer.value);
  }

  const updateTimeRemaining = () => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;

    if (diff <= 0) {
      timeRemaining.value = 'Expired';
      clearInterval(expirationTimer.value);
      return;
    }

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    timeRemaining.value = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  updateTimeRemaining();
  expirationTimer.value = setInterval(updateTimeRemaining, 1000);
};

const startResendTimer = (nextResendTime) => {
  if (resendTimer.value) {
    clearInterval(resendTimer.value);
  }

  canResend.value = false;

  const updateResendTime = () => {
    const now = new Date();
    const nextResend = new Date(nextResendTime);
    const diff = nextResend - now;

    if (diff <= 0) {
      canResend.value = true;
      timeUntilResend.value = '';
      clearInterval(resendTimer.value);
      return;
    }

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    timeUntilResend.value = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  updateResendTime();
  resendTimer.value = setInterval(updateResendTime, 1000);
};

const reset = () => {
  codeDigits.value = ['', '', '', '', '', ''];
  hasError.value = false;
  errorMessage.value = '';
  if (inputRefs.value[0]) {
    focusInput(0);
  }
};

onMounted(() => {
  // Focus first input on mount
  if (inputRefs.value[0]) {
    setTimeout(() => focusInput(0), 100);
  }

  // Start timers if props are provided
  if (props.codeExpiresAt) {
    startExpirationTimer(props.codeExpiresAt);
  }
  if (props.nextResendAvailable) {
    startResendTimer(props.nextResendAvailable);
  }
});

onUnmounted(() => {
  if (expirationTimer.value) {
    clearInterval(expirationTimer.value);
  }
  if (resendTimer.value) {
    clearInterval(resendTimer.value);
  }
});

// Expose reset method for parent component
defineExpose({
  reset
});
</script>

<style scoped>
.verification-code-container {
  position: relative;
  padding: 20px;
}

.code-header {
  text-align: center;
  margin-bottom: 24px;
}

.code-header h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  color: #111827;
  font-weight: 600;
}

.code-instructions {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
}

.code-input-wrapper {
  margin-bottom: 16px;
}

.code-inputs {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
}

.code-input {
  width: 48px;
  height: 56px;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  background: white;
  transition: all 0.2s;
}

.code-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.code-input.error {
  border-color: #ef4444;
  background-color: #fef2f2;
}

.error-message {
  text-align: center;
  color: #ef4444;
  font-size: 14px;
  margin-bottom: 12px;
  min-height: 20px;
}

.attempts-remaining {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #dc2626;
}

.expiration-info {
  text-align: center;
  margin-bottom: 16px;
  font-size: 14px;
  color: #6b7280;
}

.expiration-label {
  margin-right: 8px;
}

.expiration-time {
  font-weight: 600;
  color: #3b82f6;
}

.code-actions {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.resend-button {
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.resend-button:hover:not(:disabled) {
  background-color: #eff6ff;
}

.resend-button:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-overlay p {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

@media (max-width: 480px) {
  .code-inputs {
    gap: 8px;
  }

  .code-input {
    width: 40px;
    height: 48px;
    font-size: 20px;
  }
}
</style>
